/**
 * Permission Utilities
 * Centralizes owner-level permission checks for consistent access control
 */

// Primary owner emails that always have full access
export const OWNER_EMAILS = ['todayskidz@comcast.net', 'todayskidz45@gmail.com'];

/**
 * Check if a user has owner-level permissions
 * This grants owner access to:
 * - Users with role 'owner' or 'co-owner'
 * - Users with viewAsTier set (staff testing tiers as simulated owners)
 * - Users with isTestAccount flag (test accounts are organization owners)
 * - Users whose email is in OWNER_EMAILS list
 * 
 * @param {Object} user - The user object from useAuth()
 * @returns {boolean} - Whether user has owner-level permissions
 */
export const hasOwnerPermissions = (user) => {
  if (!user) return false;
  
  // Check if user email is a primary owner
  const userEmail = user.email?.toLowerCase();
  if (userEmail && OWNER_EMAILS.includes(userEmail)) {
    return true;
  }
  
  // Check if user role is owner or co-owner
  const role = user.role?.toLowerCase();
  if (role === 'owner' || role === 'co-owner') {
    return true;
  }
  
  // Check if user has viewAsTier (staff simulating tier as an owner)
  if (user.viewAsTier) {
    return true;
  }
  
  // Check if user is a test account (test accounts are organization owners)
  if (user.isTestAccount === true) {
    return true;
  }
  
  // Check isOwner flag
  if (user.isOwner === true) {
    return true;
  }
  
  return false;
};

/**
 * Check if a user is strictly an owner (not co-owner or test account)
 * Use this for very sensitive operations that only the primary owner should perform
 * 
 * @param {Object} user - The user object from useAuth()
 * @returns {boolean} - Whether user is strictly an owner
 */
export const isStrictOwner = (user) => {
  if (!user) return false;
  
  const userEmail = user.email?.toLowerCase();
  if (userEmail && OWNER_EMAILS.includes(userEmail)) {
    return true;
  }
  
  return user.role?.toLowerCase() === 'owner';
};

/**
 * Check if user can manage children (add, edit, delete)
 * This is a commonly used permission check
 * 
 * @param {Object} user - The user object from useAuth()
 * @returns {boolean}
 */
export const canManageChildren = (user) => {
  return hasOwnerPermissions(user);
};

/**
 * Check if user can access owner tools
 * Only primary owners can access this
 * 
 * @param {Object} user - The user object from useAuth()
 * @returns {boolean}
 */
export const canAccessOwnerTools = (user) => {
  if (!user) return false;
  const userEmail = user.email?.toLowerCase();
  return userEmail && OWNER_EMAILS.includes(userEmail);
};

/**
 * Check if user can add or delete schools (Director/Owner, Admin/Co-Owner,
 * or Business Manager). Other staff can still select an existing school on
 * a child record — this only gates the Manage Schools add/delete actions.
 *
 * @param {Object} user - The user object from useAuth()
 * @returns {boolean}
 */
export const canManageSchools = (user) => {
  if (hasOwnerPermissions(user)) return true;
  return (user?.role || '').toLowerCase() === 'business-manager';
};

export default {
  OWNER_EMAILS,
  hasOwnerPermissions,
  isStrictOwner,
  canManageChildren,
  canAccessOwnerTools,
  canManageSchools
};
